/**
 * Module Editor Component
 * Handles creation and editing of course modules
 */
import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Progress } from '../ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Video,
  FileText,
  Link as LinkIcon,
  Upload,
  Loader2,
  CheckCircle2,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ModuleEditor = ({ 
  open, 
  onOpenChange, 
  course, 
  onSave,
  initialModule = null 
}) => {
  const { t } = useTranslation();
  const videoInputRef = useRef(null);
  
  const [moduleForm, setModuleForm] = useState(initialModule || {
    title: '',
    type: 'video',
    content: '',
    video_url: '',
    duration_minutes: 10,
    order: course?.modules?.length + 1 || 1,
  });
  
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 500MB)
    if (file.size > 500 * 1024 * 1024) {
      toast.error(t('courses.videoTooLarge', 'Video file is too large. Maximum size is 500MB.'));
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/upload/video`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      });

      const videoUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      setModuleForm(prev => ({ ...prev, video_url: videoUrl }));
      toast.success(t('courses.videoUploaded', 'Video uploaded successfully'));
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(t('courses.uploadFailed', 'Failed to upload video'));
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!moduleForm.title.trim()) {
      toast.error(t('courses.moduleTitleRequired', 'Module title is required'));
      return;
    }

    setSaving(true);
    try {
      await onSave(moduleForm);
      onOpenChange(false);
      setModuleForm({
        title: '',
        type: 'video',
        content: '',
        video_url: '',
        duration_minutes: 10,
        order: course?.modules?.length + 1 || 1,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {initialModule 
              ? t('courses.editModule', 'Edit Module')
              : t('courses.addModule', 'Add Module')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>{t('courses.moduleTitle', 'Module Title')}</Label>
            <Input
              value={moduleForm.title}
              onChange={(e) => setModuleForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder={t('courses.moduleTitlePlaceholder', 'Enter module title')}
              data-testid="module-title-input"
            />
          </div>

          <div className="space-y-2">
            <Label>{t('courses.moduleType', 'Type')}</Label>
            <Select
              value={moduleForm.type}
              onValueChange={(value) => setModuleForm(prev => ({ ...prev, type: value }))}
            >
              <SelectTrigger data-testid="module-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="video">
                  <span className="flex items-center gap-2">
                    <Video className="w-4 h-4" />
                    {t('courses.video', 'Video')}
                  </span>
                </SelectItem>
                <SelectItem value="text">
                  <span className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    {t('courses.text', 'Text')}
                  </span>
                </SelectItem>
                <SelectItem value="link">
                  <span className="flex items-center gap-2">
                    <LinkIcon className="w-4 h-4" />
                    {t('courses.link', 'External Link')}
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Video Upload */}
          {moduleForm.type === 'video' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t('courses.videoUrl', 'Video URL')}</Label>
                <Input
                  value={moduleForm.video_url}
                  onChange={(e) => setModuleForm(prev => ({ ...prev, video_url: e.target.value }))}
                  placeholder="https://youtube.com/... or upload"
                  data-testid="module-video-url"
                />
              </div>

              <div className="text-center text-sm text-muted-foreground">
                {t('common.or', 'or')}
              </div>

              <div>
                <input
                  type="file"
                  ref={videoInputRef}
                  className="hidden"
                  accept="video/*"
                  onChange={handleVideoUpload}
                />
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => videoInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="upload-video-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('courses.uploading', 'Uploading...')} {uploadProgress}%
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      {t('courses.uploadVideo', 'Upload Video')}
                    </>
                  )}
                </Button>
                {uploading && <Progress value={uploadProgress} className="mt-2" />}
              </div>
            </div>
          )}

          {/* Text Content */}
          {moduleForm.type === 'text' && (
            <div className="space-y-2">
              <Label>{t('courses.content', 'Content')}</Label>
              <Textarea
                value={moduleForm.content}
                onChange={(e) => setModuleForm(prev => ({ ...prev, content: e.target.value }))}
                placeholder={t('courses.contentPlaceholder', 'Enter module content')}
                rows={6}
                data-testid="module-content-textarea"
              />
            </div>
          )}

          {/* External Link */}
          {moduleForm.type === 'link' && (
            <div className="space-y-2">
              <Label>{t('courses.externalLink', 'External Link')}</Label>
              <Input
                value={moduleForm.content}
                onChange={(e) => setModuleForm(prev => ({ ...prev, content: e.target.value }))}
                placeholder="https://..."
                data-testid="module-link-input"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>{t('courses.duration', 'Duration (minutes)')}</Label>
            <Input
              type="number"
              value={moduleForm.duration_minutes}
              onChange={(e) => setModuleForm(prev => ({ 
                ...prev, 
                duration_minutes: parseInt(e.target.value) || 0 
              }))}
              min={1}
              data-testid="module-duration-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4 mr-2" />
            )}
            {t('common.save', 'Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ModuleEditor;
